<div class="dialog-header">
  <div class="dialog-icon-wrapper">
    <div class="dialog-icon new">
      <i class="fa-solid fa-plus"></i>
    </div>
    <div>
      <h2 class="dialog-title">New TV Commercial</h2>
      <p class="dialog-subtitle">Fill in the commercial details</p>
    </div>
  </div>
  <button class="dialog-close" (click)="onCancel()">
    <i class="fa-solid fa-xmark"></i>
  </button>
</div>

<mat-dialog-content class="dialog-body">
  <form [formGroup]="commercialForm" class="dialog-form-grid">
    <!-- Commercial Type -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Commercial Type <span class="req">*</span></mat-label>
      <mat-form-field appearance="outline">
        <mat-select formControlName="commercialType" placeholder="Select type" [compareWith]="comparatorService.compareById">
          @for( type of commercialTypes; track type ) {
            <mat-option [value]="type">
              {{ type | objectdesc }}
            </mat-option>
          }
        </mat-select>
        @if( commercialForm.get('commercialType')?.hasError('required') ) {
          <mat-error >Commercial type is required</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Category -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Category <span class="req">*</span></mat-label>
      <mat-form-field appearance="outline">
        <mat-select formControlName="category" placeholder="Select category" [compareWith]="comparatorService.compareById">
          @for( category of categories; track category ) {
            <mat-option [value]="category">
              {{ category | objectdesc}}
            </mat-option>
          }
        </mat-select>
        @if( commercialForm.get('category')?.hasError('required') ) {
          <mat-error >Category is required</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Commercial Office -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Commercial Office <span class="req">*</span></mat-label>
      <mat-form-field appearance="outline">
        <mat-select formControlName="commercialOffice" placeholder="Select office" (valueChange)="onChangeCommercialOffice($event)" [compareWith]="comparatorService.compareById">
          @for( office of commercialOffices; track office ) {
            <mat-option [value]="office">{{ office | objectdesc}}</mat-option>
          }
        </mat-select>
        @if( commercialForm.get('commercialOffice')?.hasError('required') ) {
          <mat-error >Commercial office is required</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Agency -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Agency <span class="req">*</span></mat-label>
      <mat-form-field appearance="outline">
        <mat-select formControlName="agency" placeholder="Select agency" (valueChange)="onChangeAgency($event)" [compareWith]="comparatorService.compareById">
          @for( agency of agencies; track agency ) {
            <mat-option [value]="agency">{{ agency | objectdesc}}</mat-option>
          }
        </mat-select>
        @if( commercialForm.get('agency')?.hasError('required') ) {
          <mat-error >Agency is required</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Brand/Advertiser -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Brand/Advertiser <span class="req">*</span></mat-label>
      <mat-form-field appearance="outline">
        <input
          type="text"
          matInput
          formControlName="brandAdvertiser"
          placeholder="Type to search brand"
          [matAutocomplete]="acBrand">
        <mat-autocomplete #acBrand="matAutocomplete">
          @for( brand of brands; track brand ) {
            <mat-option [value]="brand">{{ brand }}</mat-option>
          }
        </mat-autocomplete>
        @if( commercialForm.get('brandAdvertiser')?.hasError('required') ) {
          <mat-error >Brand/Advertiser is required</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Product -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Product <span class="req">*</span></mat-label>
      <mat-form-field appearance="outline">
        <input
          type="text"
          matInput
          formControlName="product"
          placeholder="Type to search product"
          [matAutocomplete]="acProduct">
        <mat-autocomplete #acProduct="matAutocomplete">
          @for( product of products; track product ) {
            <mat-option [value]="product">{{ product }}</mat-option>
          }
        </mat-autocomplete>
        @if( commercialForm.get('product')?.hasError('required') ) {
          <mat-error >Product is required</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Reference Title -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Reference Title <span class="req">*</span></mat-label>
      <mat-form-field appearance="outline">
        <input matInput formControlName="referenceTitle" placeholder="Enter reference title">
        @if( commercialForm.get('referenceTitle')?.hasError('required') ) {
          <mat-error >Reference title is required</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Commercial Attributes -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Commercial Attributes</mat-label>
      <mat-form-field appearance="outline">
        <mat-select formControlName="commercialAttributes" placeholder="Select attributes" multiple [compareWith]="comparatorService.compareById">
          <mat-select-trigger>
            @if (commercialForm.get('commercialAttributes')?.value?.length > 0) {
              {{ commercialForm.get('commercialAttributes')?.value[0] | objectdesc}}
              @if (commercialForm.get('commercialAttributes')?.value?.length > 1) {
                <span class="additional-selection">
                  (+{{ commercialForm.get('commercialAttributes')?.value.length - 1 }}
                  {{ commercialForm.get('commercialAttributes')?.value.length === 2 ? 'other' : 'others' }})
                </span>
              }
            }
          </mat-select-trigger>
          @for( attribute of commercialAttributes; track attribute ) {
            <mat-option [value]="attribute">
              {{ attribute | objectdesc}}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>

    <!-- Language, CRT, and Duration in one row -->
    <div class="dialog-form-grid-full" style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px;">
      <!-- Language -->
      <div class="dialog-field-group">
        <mat-label class="mat-form-field-label">Language <span class="req">*</span></mat-label>
        <mat-form-field appearance="outline">
          <mat-select formControlName="language" placeholder="Select language" (valueChange)="onChangeLanguage($event)" [compareWith]="comparatorService.compareById">
            @for( language of languages; track language ) {
              <mat-option [value]="language">
                {{ language | objectdesc}}
              </mat-option>
            }
          </mat-select>
          @if( commercialForm.get('language')?.hasError('required') ) {
            <mat-error >Language is required</mat-error>
          }
        </mat-form-field>
      </div>

      <!-- CRT -->
      <div class="dialog-field-group">
        <mat-label class="mat-form-field-label">CRT <span class="req">*</span></mat-label>
        <mat-form-field appearance="outline">
          <input matInput type="text" formControlName="crt" placeholder="Enter CRT" maxlength="15" pattern="[0-9]*">
          @if( commercialForm.get('crt')?.hasError('required') ) {
            <mat-error >CRT is required</mat-error>
          }
          @if( commercialForm.get('crt')?.hasError('pattern') ) {
            <mat-error >CRT must contain only numbers</mat-error>
          }
          @if( commercialForm.get('crt')?.hasError('maxlength') ) {
            <mat-error >CRT must be 15 characters or less</mat-error>
          }
        </mat-form-field>
      </div>

      <!-- Duration -->
      <div class="dialog-field-group">
        <mat-label class="mat-form-field-label">Duration (seconds) <span class="req">*</span></mat-label>
        <mat-form-field appearance="outline">
          <input matInput type="number" formControlName="duration" placeholder="Enter duration" min="1">
          @if( commercialForm.get('duration')?.hasError('required') ) {
            <mat-error >Duration is required</mat-error>
          }
          @if( commercialForm.get('duration')?.hasError('min') ) {
            <mat-error >Duration must be greater than 0</mat-error>
          }
        </mat-form-field>
      </div>
    </div>

    <!-- Upload File Using -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Upload File Using <span class="req">*</span></mat-label>
      <mat-form-field appearance="outline">
        <mat-select formControlName="uploadMethod" placeholder="Select method" [compareWith]="comparatorService.compareById" (valueChange)="onChangeUploadMethod($event)">
          @for( method of uploadMethods; track method ) {
            <mat-option [value]="method">{{ method | objectdesc}}</mat-option>
          }
        </mat-select>
        @if( commercialForm.get('uploadMethod')?.hasError('required') ) {
          <mat-error>Upload method is required</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- File -->
     @if( commercialForm.get('uploadMethod') != null && commercialForm.get('uploadMethod')?.value.id == 1 ) {
      <file-selector [fileFormControl]="$any(commercialForm.get('file'))" [label]="'Media File'" [required]="true" [multiple]="false" (fileSelected)="onSelectFile($event)"></file-selector>
     }
     @if( commercialForm.get('uploadMethod') != null && commercialForm.get('uploadMethod')?.value.id == 2) {
      <aspera-file-selector [fileFormControl]="$any(commercialForm.get('file'))" [label]="'Media File'" [required]="true" [multiple]="false" (fileSelected)="onSelectFileAspera($event)"></aspera-file-selector>
     }
  </form>
</mat-dialog-content>

<mat-dialog-actions class="dialog-footer">
  <button class="btn-ghost" (click)="onCancel()">Cancel</button>
  <button class="btn-blue" (click)="onSubmit()" [disabled]="!commercialForm.valid">
    Create Commercial
  </button>
</mat-dialog-actions>
